/**
 * conflictStore — estado global da resolução de conflitos 3-way.
 *
 * Fluxo:
 *   begin() → startConflictResolution (conflictFlow) → status='modal' → apply() → commit
 */

import { create } from 'zustand'
import type { MergeResult } from '@/services/merge'
import type { ResolutionSet } from '@/services/merge/resolve'

export type ConflictFlowStatus = 'idle' | 'loading' | 'modal' | 'committing' | 'error'

interface ConflictStore {
  active: boolean
  status: ConflictFlowStatus
  projectId: number | null
  mergeResult: MergeResult | null
  pendingCommitName: string | null
  pendingCommitDescription: string | null
  headCommitId: number | null
  error: string | null
  retryCount: number

  /** Inicia o fluxo de resolução. Aciona startConflictResolution via dynamic import. */
  begin: (opts: {
    projectId: number
    commitName?: string
    commitDescription?: string
    /** HEAD autoritativo (ex.: current_commit_id da resposta 409). */
    headCommitId?: number
  }) => Promise<void>

  /** Aplica resoluções e commita. Chamado pelo ConflictResolverModal.onApply. */
  apply: (resolutionSet: ResolutionSet) => Promise<void>

  /** Cancela o fluxo (escape hatch). */
  cancel: () => void

  reset: () => void
}

export const useConflictStore = create<ConflictStore>((set, get) => ({
  active: false,
  status: 'idle',
  projectId: null,
  mergeResult: null,
  pendingCommitName: null,
  pendingCommitDescription: null,
  headCommitId: null,
  error: null,
  retryCount: 0,

  async begin({ projectId, commitName, commitDescription, headCommitId }) {
    set({
      active: true,
      status: 'loading',
      projectId,
      pendingCommitName: commitName ?? null,
      pendingCommitDescription: commitDescription ?? null,
      headCommitId: headCommitId ?? null,
      error: null,
    })
    const { startConflictResolution } = await import('@/services/merge/conflictFlow')
    await startConflictResolution(projectId, headCommitId)
  },

  async apply(resolutionSet) {
    const { projectId } = get()
    if (!projectId) return
    const { finishConflictResolution } = await import('@/services/merge/conflictFlow')
    await finishConflictResolution(projectId, resolutionSet)
  },

  cancel() {
    get().reset()
  },

  reset() {
    set({
      active: false,
      status: 'idle',
      projectId: null,
      mergeResult: null,
      pendingCommitName: null,
      pendingCommitDescription: null,
      headCommitId: null,
      error: null,
      retryCount: 0,
    })
  },
}))
